import { useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { setTenantId } from './lib/tenantStore';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { hasSession } from './lib/auth';
import { queryClient } from './lib/queryClient';
import { ensureProfile, type MyProfile } from './lib/profile';
import { canSeeTab, ensureTabPermissions, startPageOf, TAB_PATHS } from './lib/tabPermissions';
import { getSuperAdminClubId } from './lib/superAdminStore';
import { getTenantId } from './lib/tenantStore';
import { FullStorySession } from './components/FullStorySession';
import { LegalNoticeGate } from './components/legal/LegalNoticeGate';

// ── Lazy pages (H13) ──────────────────────────────────────────────────────────
// Every page behind the auth gates is code-split into its own chunk: previously
// routeTree statically imported all 24 pages, producing a single ~2 MB bundle
// (AdminPage alone is 4,670 lines). Login stays in the entry chunk — it IS the first paint.

const OnboardingPage = lazyRouteComponent(() => import('./pages/OnboardingPage'), 'OnboardingPage');
const InjuryRegistryPage = lazyRouteComponent(() => import('./pages/InjuryRegistryPage'), 'InjuryRegistryPage');
const PassportPage = lazyRouteComponent(() => import('./pages/PassportPage'), 'PassportPage');
const IntegrationHubPage = lazyRouteComponent(() => import('./pages/IntegrationHubPage'), 'IntegrationHubPage');
const PromptLabPage = lazyRouteComponent(() => import('./pages/PromptLabPage'), 'PromptLabPage');
const NotificationsPage = lazyRouteComponent(() => import('./pages/NotificationsPage'), 'NotificationsPage');
const SessionsPage = lazyRouteComponent(() => import('./pages/SessionsPage'), 'SessionsPage');
const ReportsPage = lazyRouteComponent(() => import('./pages/ReportsPage'), 'ReportsPage');
const LogsPage = lazyRouteComponent(() => import('./pages/LogsPage'), 'LogsPage');
const AdminPage = lazyRouteComponent(() => import('./pages/AdminPage'), 'AdminPage');
const MethodologyPage = lazyRouteComponent(() => import('./pages/MethodologyPage'), 'MethodologyPage');
const PerformancePage = lazyRouteComponent(() => import('./pages/PerformancePage'), 'PerformancePage');
const GuidancePage = lazyRouteComponent(() => import('./pages/GuidancePage'), 'GuidancePage');
const LivePage = lazyRouteComponent(() => import('./pages/LivePage'), 'LivePage');
// The Player Page — ONE component behind both routes. A player_view user gets it
// bare (it is their whole app); staff get the same page inside the Layout with
// the wellness check-in read-only. It retired PlayerPage + StaffPlayerDetailPage.
const PlayerProfilePage = lazyRouteComponent(
  () => import('./pages/playerprofile/PlayerProfilePage'),
  'PlayerProfilePage'
);
const StaffPlayerProfilePage = lazyRouteComponent(
  () => import('./pages/playerprofile/PlayerProfilePage'),
  'StaffPlayerProfilePage'
);
const SuperAdminPage = lazyRouteComponent(() => import('./pages/SuperAdminPage'), 'SuperAdminPage');
const ChooseClubPage = lazyRouteComponent(() => import('./pages/ChooseClubPage'), 'ChooseClubPage');
const ChangePasswordPage = lazyRouteComponent(() => import('./pages/ChangePasswordPage'), 'ChangePasswordPage');
const DashboardPage = lazyRouteComponent(() => import('./pages/DashboardPage'), 'DashboardPage');
const SessionOverviewPage = lazyRouteComponent(
  () => import('./pages/sessionoverview/SessionOverviewPage'),
  'SessionOverviewPage',
);
// Load & Risk Room — the redesign's S&C lens (spec §8.2).
const LoadRiskPage = lazyRouteComponent(() => import('./pages/loadrisk/LoadRiskPage'), 'LoadRiskPage');
// The Data Room — per-session/per-player provenance drill-down (Activities' replacement).
const DataRoomPage = lazyRouteComponent(() => import('./pages/dataroom/DataRoomPage'), 'DataRoomPage');
// The Opponent Lab — next-match preparation surface (retires the Scouting page).
const OpponentLabPage = lazyRouteComponent(
  () => import('./pages/opponentlab/OpponentLabPage'),
  'OpponentLabPage'
);
// Our Reports — dedicated own-club report browser (reuses ReportsPanel in 'own' mode), reachable
// without already being on the right opponent's Opponent Lab page.
const OurReportsPage = lazyRouteComponent(
  () => import('./pages/opponentlab/OurReportsPage'),
  'OurReportsPage'
);

// ── Guard helper (H14) ────────────────────────────────────────────────────────
// One cached profile fetch shared by all guards and Layout (['my-profile']).
// Returns null when the profile can't be loaded for a non-auth reason so each
// guard can degrade the way the old raw-axios code did.
async function guardProfile(): Promise<MyProfile | null> {
  try {
    return await ensureProfile(queryClient);
  } catch (err) {
    if ((err as { isRedirect?: boolean }).isRedirect) throw err;
    if ((err as { response?: { status: number } }).response?.status === 401) {
      throw redirect({ to: '/login' });
    }
    return null;
  }
}

// ── App gate (H14) ──────────────────────────────────────────────────────────────
// Shared active-club gating: persist the tenant context and bounce a user who
// isn't a full club member to the right place (tenant set, super-admin club chosen,
// pending → onboarding). Used by the Layout-wrapped appRoute.
async function appGate(): Promise<void> {
  const profile = await guardProfile();
  if (!profile) return;

  // Super admins choose their active club from a dedicated screen. Their tenant
  // context is set by activateClub() there, NOT from profile.tenantId (which points
  // at their own user row). Redirect if either the club or tenant context is missing.
  if (profile.role === 'super_admin') {
    if (!getSuperAdminClubId() || !getTenantId()) throw redirect({ to: '/choose-club' });
    return;
  }

  // Regular users: persist the tenant ID so every API call carries X-Tenant-Id.
  if (profile.tenantId) setTenantId(profile.tenantId);

  if (!profile.clubId || profile.role === 'pending') throw redirect({ to: '/onboarding' });
  if (profile.role === 'player_view') throw redirect({ to: '/player' });
}

// ── Root ──────────────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ── Public routes ─────────────────────────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// ── Session gate: any logged-in user ──────────────────────────────────────────

// Belt-and-suspenders auth guard: beforeLoad throws a redirect before render;
// SessionGate hard-redirects if a stale render slips through (CI edge case).
//
// The confidentiality notice sits HERE, around every signed-in route, so it comes before any page
// whatever the role and whatever page the role lands on — the staff app, the player's own page,
// onboarding, club choice and the password change alike.
function SessionGate() {
  useEffect(() => {
    if (!hasSession()) window.location.replace('/login');
  }, []);
  return (
    <>
      <FullStorySession />
      <LegalNoticeGate>
        <Outlet />
      </LegalNoticeGate>
    </>
  );
}

const sessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'session',
  beforeLoad: () => {
    if (!hasSession()) throw redirect({ to: '/login' });
  },
  component: SessionGate,
});

// Onboarding lives inside the session gate (needs login, but no club yet)
const onboardingRoute = createRoute({
  getParentRoute: () => sessionRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

// Club selector shown to super admins at login time.
// Lives inside the session gate so the user must be logged in,
// but outside the app gate because it does not require an active club.
const chooseClubRoute = createRoute({
  getParentRoute: () => sessionRoute,
  path: '/choose-club',
  component: ChooseClubPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => sessionRoute,
  path: '/change-password',
  component: ChangePasswordPage,
});

// Player dashboard — session-gated, player_view role only, no sidebar
const playerDashboardRoute = createRoute({
  getParentRoute: () => sessionRoute,
  path: '/player',
  beforeLoad: async () => {
    const profile = await guardProfile();
    // Non-player roles go back to the staff app
    if (profile?.role && profile.role !== 'player_view') {
      throw redirect({ to: '/' });
    }
  },
  component: PlayerProfilePage,
});

// ── App gate: active club member ──────────────────────────────────────────────

const appRoute = createRoute({
  getParentRoute: () => sessionRoute,
  id: 'app',
  beforeLoad: appGate,
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

// ── Protected pages ───────────────────────────────────────────────────────────

// Default landing: / redirects to the first page the user can open (TAB_PATHS order — the
// Dashboard first). Sign-in navigates to '/', so this is where a user lands once connected.
// appGate (appRoute.beforeLoad) runs first, so the tenant context is set before the redirect fires.
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  // Sends the user to the first page they can actually open. Hardcoding /matchweek meant
  // a role without that page bounced here, got sent to /matchweek, was refused, and came
  // straight back — an infinite redirect that rendered as a blank screen.
  beforeLoad: async () => { throw redirect({ to: await firstVisiblePath() }); },
  component: () => null,
});

// Matchweek is RETIRED (readiness plan Phase 7): its verdicts, reasons and pending decisions live on the Dashboard,
// its next-match plan in the Opponent Lab. Old links and bookmarks land on the Dashboard, whose own route guard then
// decides — the same pattern as /scouting.
const matchweekRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/matchweek',
  beforeLoad: () => { throw redirect({ to: '/dashboard' }); },
  component: () => null,
});

// Nav keys paired with their paths, in the sidebar's own order — used to pick a landing
// page and a refusal destination. Admin is the terminal fallback because it is visible to
// every role (it hosts the per-user Preferences section), which guarantees termination.

/**
 * First page this user may open. Never returns '/' — that would re-enter the router.
 *
 * The role's START PAGE comes first when the club chose one (Admin → Permissions, 2026-10: the
 * fitness coach opens on the session overview while the head coach keeps the Dashboard), but only
 * while the role may still open it — a start page unticked later falls back to the nav order rather
 * than sending anyone to a page that would refuse them.
 */
async function firstVisiblePath(exclude?: string): Promise<string> {
  const profile = await guardProfile();
  const perms = await ensureTabPermissions(queryClient);
  const start = startPageOf(perms);
  const startPath = TAB_PATHS.find(([key]) => key === start)?.[1];
  if (start && startPath && start !== exclude && canSeeTab(start, profile?.role, perms)) {
    return startPath;
  }
  const hit = TAB_PATHS.find(([key]) => key !== exclude && canSeeTab(key, profile?.role, perms));
  return hit?.[1] ?? '/admin';
}

// Page guard driven by the club's permission matrix: a page hidden in Admin →
// Permissions must not be reachable by typing its URL either.
//
// EVERY keyed nav page carries this guard. It is not optional and not a per-page
// judgement call: the sidebar filter and the route guard are two halves of ONE
// decision, and a page that has only the first half is hidden from the nav while
// staying wide open to anyone who types the URL. That is what happened to /live,
// /dashboard, /reports, /injuries, /passport, /integration and /prompt-lab — an
// admin unticked Live for the analyst role, the nav item vanished, and typing
// /live opened the page. `src/__tests__/routeGuards.test.ts` walks this tree and
// invokes each guard, so a new page added without one fails the build.
//
// A refusal redirects to another page the user CAN see — never to '/', which redirects
// onward and would loop, and never back to the page just refused.
async function requireTab(tabKey: string) {
  const profile = await guardProfile();
  const perms = await ensureTabPermissions(queryClient);
  if (canSeeTab(tabKey, profile?.role, perms)) return;
  throw redirect({ to: await firstVisiblePath(tabKey) });
}

// Load & Risk Room — gated to the S&C role set. The FE guard is UX-level; the
// ledger/calibration endpoints enforce the same roles server-side (the truth).
const loadRiskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/load-risk',
  beforeLoad: () => requireTab('loadrisk'),
  component: LoadRiskPage,
});

// The Data Room — session/player provenance drill-down (Activities' replacement).
const dataRoomRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/data-room',
  beforeLoad: () => requireTab('dataroom'),
  component: DataRoomPage,
});

// The Opponent Lab — next-match prep surface. It replaces the Scouting page.
const opponentLabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/opponent-lab',
  beforeLoad: () => requireTab('scouting'),
  component: OpponentLabPage,
});

// Our Reports — own-club report browser, gated under the same `scouting` tab as the Opponent Lab
// (not a new permission surface).
const ourReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/opponent-lab/our-reports',
  beforeLoad: () => requireTab('scouting'),
  component: OurReportsPage,
});

// Dashboard (morning briefing) — un-retired; reachable via its nav item at /dashboard.
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  beforeLoad: () => requireTab('dashboard'),
  component: DashboardPage,
});

// The session overview (2026-10) — one session against the season's sessions of its MD label.
const sessionOverviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/session-overview',
  beforeLoad: () => requireTab('sessionOverview'),
  component: SessionOverviewPage,
});

// Legacy surfaces folded into the Load & Risk Room (redesign §8.2): /heatmap,
// /inbox, /hub, /violations, /attribution all land there now.
const heatmapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/heatmap',
  beforeLoad: () => { throw redirect({ to: '/load-risk' }); },
  component: () => null,
});

const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox',
  beforeLoad: () => { throw redirect({ to: '/load-risk' }); },
  component: () => null,
});

const hubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/hub',
  beforeLoad: () => { throw redirect({ to: '/load-risk' }); },
  component: () => null,
});

const violationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/violations',
  beforeLoad: () => { throw redirect({ to: '/load-risk' }); },
  component: () => null,
});

const attributionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/attribution',
  beforeLoad: () => { throw redirect({ to: '/load-risk' }); },
  component: () => null,
});

const injuriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/injuries',
  beforeLoad: () => requireTab('injuries'),
  component: InjuryRegistryPage,
});

const passportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/passport',
  beforeLoad: () => requireTab('passport'),
  component: PassportPage,
});

const integrationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/integration',
  beforeLoad: () => requireTab('integration'),
  component: IntegrationHubPage,
});

const promptLabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/prompt-lab',
  beforeLoad: () => requireTab('promptLab'),
  component: PromptLabPage,
});

const sessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sessions',
  component: SessionsPage,
});

// Legacy: Activities is retired in favor of the Data Room.
const activitiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activities',
  beforeLoad: () => { throw redirect({ to: '/data-room' }); },
  component: () => null,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  beforeLoad: () => requireTab('reports'),
  component: ReportsPage,
});

const logsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/logs',
  beforeLoad: () => requireTab('logs'),
  component: LogsPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: NotificationsPage,
});

// Every authenticated member can reach /admin — it also hosts the per-user
// Preferences section (language). AdminPage itself gates the actual admin
// tools (users, thresholds, players, …) behind the admin/super_admin check;
// non-admins only ever see the Preferences tab.
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  component: AdminPage,
});

const methodologyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/methodology',
  component: MethodologyPage,
});

const performanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/performance',
  component: PerformancePage,
});

const guideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/guide',
  component: GuidancePage,
});

// Legacy: the Scouting page is retired in favor of the Opponent Lab.
const scoutingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/scouting',
  beforeLoad: () => { throw redirect({ to: '/opponent-lab' }); },
  component: () => null,
});

const liveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/live',
  beforeLoad: () => requireTab('live'),
  component: LivePage,
});

const staffPlayerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/players/$playerId',
  component: StaffPlayerProfilePage,
});

const superAdminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/super-admin',
  beforeLoad: async () => {
    const profile = await guardProfile();
    if (profile?.role !== 'super_admin') throw redirect({ to: '/' });
  },
  component: SuperAdminPage,
});

// ── Tree ──────────────────────────────────────────────────────────────────────

export const routeTree = rootRoute.addChildren([
  loginRoute,
  sessionRoute.addChildren([
    onboardingRoute,
    chooseClubRoute,
    changePasswordRoute,
    playerDashboardRoute,
    appRoute.addChildren([
      indexRoute,
      matchweekRoute,
      loadRiskRoute,
      dataRoomRoute,
      opponentLabRoute,
      ourReportsRoute,
      hubRoute,
      violationsRoute,
      injuriesRoute,
      attributionRoute,
      passportRoute,
      dashboardRoute,
      sessionOverviewRoute,
      heatmapRoute,
      inboxRoute,
      sessionsRoute,
      activitiesRoute,
      reportsRoute,
      integrationRoute,
      logsRoute,
      promptLabRoute,
      notificationsRoute,
      adminRoute,
      methodologyRoute,
      performanceRoute,
      guideRoute,
      scoutingRoute,
      liveRoute,
      staffPlayerDetailRoute,
      superAdminRoute,
    ]),
  ]),
]);
